import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { AccordionItem } from "../AccordianItem";
import MGAAPI from "../../api/index.js";
import { useAtom } from "jotai";
import displayError from "../../constants/utils.js";
import { useForm } from "@mantine/form";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom.js";
import { componentsAtom } from "../../utils/atoms/componentsAtom.js";
import SnackBarJs from "../SnackBar.js/index.js";
import IncentivesTable from "./incentivesTable.js";
import GensetConfigWrapper from "./gensetConfigWrapper.js";
import _, { set } from "lodash";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom.js";
import { componentStatusAtom } from "../../utils/atoms/componentStatus.js";
import { Grid, TextInput, useMantineColorScheme } from "@mantine/core";
import PropTypes from "prop-types";
import { formatInputValue, mgaRegex, responsiveCol, useAssessmentRouteParams, useCurrentDesignComponentLists } from "../../constants/helper.js";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "../../utils/errorUtils.js";
import { useASelectedCountry } from "../../hooks/useASelectedCountry";
import { IconPercentage } from "@tabler/icons-react";
import gensetJson from "../../components/help/helpCardMenu.json";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom.js";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur.js";
import { helpAtom } from "../../utils/atoms/helpAtom.js";
import SEConfirmModal from "../common/SEConfirmModal.js";
import { navigateToComponentAtom } from "../../utils/atoms/navigateToComponentAtom.js";

const MGA_API = new MGAAPI();

const GensetConfiguration = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { setLoadingComp, onNavigateToComponent } = props;
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const { colorScheme } = useMantineColorScheme();

  const [projectDetails] = useAtom(projectDetailsAtom);
  const [projectComponents, setProjectComponents] = useAtom(componentsAtom);
  const [incentives, setIncentives] = useState([]);
  const [snackBar, setSnackBar] = useState({ type: "", message: "" });
  const [batteryList, setBatteryList] = useState([]);
  const snackBarRef = useRef(null);
  const [customButton, setCustomButton] = useState(false);
  const [batteryListData, setBatteryListData] = useState([]);
  const [visibleDetails, setVisibleDetails] = useState({});
  const [gensetParameters, setGensetParameters] = useState({});
  const [addStatus, setAddStatus] = useState(false);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);
  const [popupStatus, setPopupStatus] = useState({
    addAssetPopupStatus: false,
    viewAssetPopupStatus: false,
    deleteConfirmPopUpStatus: false,
  });
  const [projectTypes, setProjectTypes] = useState([]);
  const [asset, setAsset] = useState({
    type: "",
    value: "",
  });
  const [defaultAssetAttribute, setDefaultAssetAttribute] = useState([]);
  const [defaultAvailableAttributeName, setDefaultAvailableAttributeName] =
    useState([]);
  const [, setProjectDetails] = useAtom(projectDetailsAtom);
  const [, setComponentStatus] = useAtom(componentStatusAtom);

  const [currentAvailableAttributes, setCurrentAvailableAttributes] = useState(
    []
  );
  const [assetDetails, setAssetDetails] = useState({
    assets: [],
    assetsData: [],
    assetsType: [],
    assetAttributes: [],
  });
  const [isExtendedLibrary, setIsExtendedLibrary] = useState(false);
  const [assetInfo, setAssetInfo] = useState({
    allManufacturerList: [],
    manufacturerList: {},
  });
  const [updatedAsset, setUpdatedAsset] = useState({});
  const [renderFunction, setRenderFunction] = useState({
    renderAssetDetails: false,
  });
  const [editAssetData, setEditAssetData] = useState({});
  const inputRefs = useRef([]);
  const grayIconStyle = useMemo(
    () => ({
      borderRadius: "4px",
      padding: "4px",
    }),
    [colorScheme],
  );
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const containerRef = useRef(null);
  const [thermalValidationModal, setThermalValidationModal] = useState({ open: false, type: null });
  const [, setNavigateToComponent] = useAtom(navigateToComponentAtom);

  const [helpMenuData] = useAtom(helpContextMenuAtom);
    
  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.genset?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  const cleanedGenset = useMemo(() => {
    try {
      return JSON.parse(JSON.stringify(projectComponents.genset)) || {};
    } catch (error) {
      console.error("Error serializing projectComponents.genset:", error);
      return {};
    }
  }, [projectComponents.genset]);

  const form = useForm({
    key: JSON.stringify(cleanedGenset),
    initialValues: {
      ...projectComponents?.genset,
      componentAttributes: {
        ...projectComponents?.genset?.componentAttributes,
        gensetParameters: {
          ...projectComponents?.genset?.componentAttributes?.gensetParameters,
          MinPowerRate:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.MinPowerRate || 30,  
        },
      },
      economicAttributes: {
        ...projectComponents?.genset?.economicAttributes,
        gensetParameters: {
          ...projectComponents?.genset?.economicAttributes?.gensetParameters,
          LifetimeHour:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.LifetimeHour || 15000,
        }
      }
    },
    validate: {
      componentAttributes: {
        gensetParameters: {
          HeatRecoveryRatio: (value) =>
           projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="BaseloadGenset" &&
            (String(value).trim() === ""
              ? t("genset.validation.heatRecoveryRequired")
              : !mgaRegex?.zeroToHundredWithDecimal.test(value)
              ? t("genset.validation.heatRecoveryRange")
              : null),
          ThermalEnergyPrice: (value) =>
            projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="BaseloadGenset" &&
           (String(value).trim() === ""
              ? t("genset.validation.thermalEnergyPriceRequired")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("genset.validation.thermalEnergyPriceRange")
              : null),
          ThermalEmissionFactor: (value) =>
            projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="BaseloadGenset" &&
            (String(value).trim() === ""
              ? t("genset.validation.thermalEmissionFactorRequired")
              : !mgaRegex?.zeroTo1000WithDecimal?.test(value)
              ? t("genset.validation.thermalEmissionFactorRange")
              : null),
          PowerReserve: (value) =>
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.NbGenset !== 1 &&
              projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="ControlledGenset"&&
            (String(value).trim() === ""
              ? t("genset.validation.powerReserveRequired")
              : !mgaRegex?.oneToHundredRegex.test(value)
              ? t("genset.validation.powerReserveRange")
              : null),
          PowElecNom: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.ratedPowerRequired")
              : !mgaRegex?.oneToNinetyNineThousandWithDecimal.test(value)
              ? t("genset.validation.ratedPowerRange")
              : null,
          DegradationFactor: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.degradationRequired")
              : value && !mgaRegex?.zeroToNinetyNineWithDecimal?.test(value)
              ? t("genset.validation.degradationRange")
              : null,
          MinPowerRate: (value) =>
            projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="ControlledGenset"&&
            (String(value).trim() === ""
              ? t("genset.validation.minPowerRequired")
              : !mgaRegex?.zeroToHundredWithDecimal.test(value)
              ? t("genset.validation.minPowerRange")
              : null),
          FuelCurveSlope: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.slopeRequired")
              : !mgaRegex?.zeroToOneWithDecimal.test(value)
              ? t("genset.validation.slopeRange")
              : null,
          FuelCurveIntercept: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.interceptRequired")
              : !mgaRegex?.zeroToHundredWithMaxFiveDecimal.test(value)
              ? t("genset.validation.interceptRange")
              : null,
          MinimumRunTime: (value) =>
            projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="ControlledGenset"&&
           (String(value).trim() === ""
              ? t("genset.validation.minRunTimeRequired")
              : !mgaRegex?.zeroToThreeHundredWithDecimal.test(value)
              ? t("genset.validation.minRunTimeRange")
              : null),
          Fuel: {
            Name: (value) => (value ? null : t("genset.validation.fuelNameRequired")),
            PricePerM3: (value) =>
              String(value).trim() === ""
                ? t("genset.validation.fuelPriceRequired")
                : !mgaRegex?.zeroPointOneTO10Power9WithDecimal.test(value)
                ? t("genset.validation.fuelPriceRange")
                : null,
            LHV: (value) =>
              String(value).trim() === ""
                ? t("genset.validation.lhvRequired")
                : !mgaRegex?.zeroTO200WithDecimal.test(value)
                ? t("genset.validation.lhvRange")
                : null,
            Density: (value) =>
              String(value).trim() === ""
                ? t("genset.validation.densityRequired")
                : !mgaRegex?.zeroTo1000WithDecimal.test(value)
                ? t("genset.validation.densityRange")
                : null,
            Carbon: (value) =>
              String(value).trim() === ""
                ? t("genset.validation.carbonRequired")
                : !mgaRegex?.zeroToHundredWithDecimal.test(value)
                ? t("genset.validation.carbonRange")
                : null,
          },
          NbGenset: (value) => (value ? null : t("genset.validation.nbGensetRequired")),
        },
      },
      economicAttributes: {
        gensetParameters: {
          CAPEX: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.capexRequired")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(value)
              ? t("genset.validation.capexRange")
              : null,
          OM: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.omRequired")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(value)
              ? t("genset.validation.omRange")
              : null,
          Replacement: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.replacementRequired")
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(value)
              ? t("genset.validation.replacementRange")
              : null,
          LifetimeHour: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.lifetimeHourRequired")
              : !mgaRegex?.zeroToNine99Regex.test(value)
              ? t("genset.validation.lifetimeHourRange")
              : null,
          InflationRate: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.inflationRequired")
              : !mgaRegex?.minusNinetyNineToPlusNinetyNineRegexWithDecimal.test(value)
              ? t("genset.validation.inflationRange")
              : null,
          LifetimeYear: (value) =>
            String(value).trim() === ""
              ? t("genset.validation.lifetimeYearRequired")
              : !mgaRegex?.oneTo25Regex.test(value)
              ? t("genset.validation.lifetimeYearRange")
              : null,
        },
      },
      uiAttributes: {
        gensetParameters: {
          // assetId: (value) =>
          //   Number(value) === 0 || value ? null : t("genset.validation.assetRequired"),
        },
      },
    },
  });

  const selectedCountry = useASelectedCountry();

  const { setValues } = form;

  const handleGensetSubmit = form.onSubmit(
    (values) => {
      SaveGenset();
    },
    (errors) => {
      const firstErrorPath = Object.keys(errors)[0];
      const inputNode = form.getInputNode(firstErrorPath);
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  );

  useImperativeHandle(ref, () => ({
    handleGensetSubmit,
  }));

  useEffect(() => {
    if (form?.values?.incentives?.length) {
      setIncentives(form?.values?.incentives);
    } else {
      const country = selectedCountry || "GEN";
      MGA_API.getPVIncentives("Baseload genset", country).then((res) => {
        setIncentives(res);
      });
    }
  }, [form?.values?.incentives, selectedCountry]);

  useEffect(() => {
    setGensetParameters(projectComponents?.genset);
    // Avoid wholesale form reset while user is editing; only hydrate when not dirty
    // if (!form.isDirty()) {
    //   form.setValues(projectComponents.genset);
    // }
  }, [projectComponents]);

  useEffect(() => {
    MGA_API.getAssetCatalogue("Generator")
      .then((res) => {
        const assetList = res?.assetCatalogue?.[0]?.assetList || [];
        setBatteryListData(res?.assetCatalogue?.[0]?.assetList);
        let filteredAsset = [];
        try {
          const userScope = JSON.parse(localStorage.getItem('scope'));
          if (userScope && userScope.access && userScope.access.saveUpdateComponent) {
            filteredAsset = [
              { value: 0, label: t("common.custom") },
              ...assetList.map((a) => ({
                label: a.manufacturer + " " + a.communicationModel,
                value: a.assetId,
              })),
            ];
          } else {
            filteredAsset = assetList.map((a) => ({
              label: a.manufacturer + " " + a.communicationModel,
              value: a.assetId,
            }));
          }
        } catch {
          filteredAsset = assetList.map((a) => ({
            label: a.manufacturer + " " + a.communicationModel,
            value: a.assetId,
          }));
        }
        setBatteryList(filteredAsset);
      })
      .catch((res) => {
        console.log("dddd", res);
        displayError("error", t("genset.fetchError"));
      });
  }, []);
  const callSnackbar = useCallback((type, message) => {
    try {
      setSnackBar({ type, message });
      snackBarRef.current.open = true;
    } catch (error) {
      console.log("error in snack bar");
    }
  }, []);

  const focusInput = (index) => {
    if (inputRefs.current[index]) {
      inputRefs.current[index].focus();
    }
  };

  useEffect(() => {
    listAssetType();
    getDefaultAssetsAttributeList();
  }, []);

  useEffect(() => {
    let allManufacturerListNew = [];
    let manufacturerListAll = {};
    if (renderFunction.renderAssetDetails) {
      assetDetails.assetsType.forEach(
        (assetTypeData) => (manufacturerListAll[assetTypeData.type] = [])
      );

      assetDetails.assets.forEach((asset) => {
        if (manufacturerListAll[asset.type.type]) {
          manufacturerListAll[asset.type.type].push(asset.manufacturer);
          manufacturerListAll[asset.type.type].sort();
        }

        if (!allManufacturerListNew.includes(asset.manufacturer)) {
          allManufacturerListNew = [
            ...allManufacturerListNew,
            asset.manufacturer,
          ];
        }
      });

      setAssetInfo((prevState) => ({
        ...prevState,
        allManufacturerList: allManufacturerListNew.sort((a, b) => a.localeCompare(b)),
        manufacturerList: manufacturerListAll,
      }));
    }
  }, [renderFunction]);

  const getDefaultAssetsAttributeList = () => {

      MGA_API.getDefaultsAssetsAttributes("Generator")
        .then((response) => {
          setDefaultAssetAttribute(response?.defaultAttributeList);
          setDefaultAvailableAttributeName(response?.allowedAttributeName);
        })
        .catch((error) => {
          console.log(error);
        });
  };

  const listAssetType = () => {

      MGA_API.getAssetType()
        .then((response) => {
          setAssetDetails((prevState) => ({
            ...prevState,
            assetsType: response.filter((a) => a.type === "Generator"),
          }));

          const renderFunctionsNew = { renderAssetDetails: true };
          setRenderFunction(renderFunctionsNew);
        })
        .catch(() => {});

  };

  const resetAssetList = () => {
    setAssetDetails((prevState) => ({
      ...prevState,
      assets: prevState.assetsData,
    }));
  };

  const addAsset = () => {
    setAddStatus(true);
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: true,
    }));
  };

  const onCloseAssetPopUp = () => {
    setPopupStatus((prevState) => ({
      ...prevState,
      addAssetPopupStatus: false,
    }));

    setAddStatus(false);
  };

  const onUpdateAsset = (data) => {
    data.attributes = [];
    const filteredAttributes = [...assetDetails.assetAttributes].filter(
      (attr) => `${attr?.value ?? ""}`.trim() !== "0"
    );
    const newAttribues = filteredAttributes.map(({ isEdit, ...rest }) => rest);
    newAttribues.forEach((attribData, index) => {
      newAttribues[index].id = 0;
      newAttribues[index].assetId = 0;
    });
    data.isExtendedLibrary = isExtendedLibrary;
    data.status = "A";
    data.assetName = "";
    data.attributes = newAttribues;
    saveAsset(data);
  };

  const saveAsset = (jsonBody) => {
    setLoadingComp(true);
    MGA_API.saveAssets(jsonBody)
      .then(async (response) => {
        resetAssetList();
        if (response?.assetId) {
          handleInputChange("uiAttributes", "assetId", response?.assetId);
          setCustomButton(false);
        }
        addUpdateDataToAssetList(response, addStatus);
        if (addStatus === true) {
          callSnackbar("success", t("genset.asset.addSuccess"));
        } else {
          callSnackbar("success", t("genset.asset.updateSuccess"));
        }
        setAssetDetails((prevState) => ({
          ...prevState,
          assetAttributes: [],
        }));
        setAddStatus(false);
        setLoadingComp(false);
        setPopupStatus((prevState) => ({
          ...prevState,
          addAssetPopupStatus: false,
        }));
        try {
          const res = await MGA_API.getAssetCatalogue("Generator");

          const assetList = res?.assetCatalogue?.[0]?.assetList || [];
          const filteredAsset = [
            { value: 0, label: t("common.custom") },
            ...assetList.map((a) => ({
              label: a.manufacturer + " " + a.communicationModel,
              value: a.assetId,
            })),
          ];
          setBatteryList(filteredAsset);
        } catch (error) {
          displayError("error", t("genset.asset.fetchError"));
        }
      })
      .catch((error) => {
        setAssetDetails((prevState) => ({
          ...prevState,
          assetAttributes: [],
        }));
        setAddStatus(false);
        setLoadingComp(false);
        setPopupStatus((prevState) => ({
          ...prevState,
          addAssetPopupStatus: false,
        }));
        
        callSnackbar("error", getErrorMessage(error));
      });
  };

  const addUpdateDataToAssetList = (data, status) => {
    if (status) {
      let newAssetList = [...assetDetails.assetsData];
      newAssetList.push(data);
      setAssetDetails((prevState) => ({
        ...prevState,
        assets: newAssetList,
        assetsData: newAssetList,
      }));
    } else {
      let assetIndex = 0;
      let newAssetList = [...assetDetails.assetsData];
      newAssetList.forEach((assetData, index) => {
        if (assetData.assetId === data.assetId) {
          assetIndex = index;
        }
      });
      if (assetIndex) {
        newAssetList[assetIndex] = data;
      }
      setAssetDetails((prevState) => ({
        ...prevState,
        assets: newAssetList,
        assetsData: newAssetList,
      }));
    }
  };

  const getAssetDetailsAttribute = (assetType) => {
    const assetId = Number(assetType);
    const assetData = [...defaultAssetAttribute];
    const selectedAssetData = assetData
      ?.filter((data) => data.id === assetId)?.[0]
      ?.defaultAttributes?.map((attr, index) => ({
        type: attr.attributeName,
        value: attr.attributeValue,
        id: index,
        isEdit: false,
      }));
    const typesToRemove = selectedAssetData?.map((item) => item.type);
    const filteredSecondArray = defaultAvailableAttributeName?.filter(
      (item) => !typesToRemove?.includes(item)
    );
    setCurrentAvailableAttributes(filteredSecondArray);
    if (addStatus) {
      setAssetDetails((prevState) => ({
        ...prevState,
        assetAttributes: selectedAssetData || [],
      }));
    }

    let attribuetsNew = assetDetails.assetsData.filter(
      (assetsData) => assetsData.type.id === parseInt(assetType,10)
    );

    let indexAssetId = 0;
    if (parseInt(assetType,10) === 2) {
      indexAssetId = 1;
    } else if (parseInt(assetType,10) === 3) {
      attribuetsNew = assetDetails.assets.filter(
        (assetsData) => assetsData.manufacturer === "STANDARD BESS TEMPLATE"
      );
      indexAssetId = 0;
    }
  };
  const handleAssetAttribute = (e, assetAttribute) => {
    const { name, value } = e.target;
    if (/^(\d+(\.\d+)?|)$/.test(value)) {
      setAssetDetails((prevState) => {
        const updateAsset = prevState.assetAttributes.map((asset) => {
          if (asset.id === assetAttribute.id) {
            return { ...asset, [name]: value };
          }
          return { ...asset };
        });
        setUpdatedAsset({ attributes: updateAsset });
        return {
          ...prevState,
          assetAttributes: updateAsset,
        };
      });
    }
  };
  const handleAsset = (e) => {
    const { name, value } = e.target;
    const checkValue = value === "" || /^\d+(\.\d+)?$/.test(value);
    if (checkValue) {
      setAsset((prev) => {
        return { ...prev, [name]: value };
      });
    }
  };
  const handleAssetSelection = (e) => {
    const { name, value } = e.target;
    setAsset((prev) => {
      return { ...prev, [name]: value };
    });
  };
  const addAttributeToLibrary = (index = 0) => {
    let newAttributeData = { id: index, ...asset };
    const uniqueName = assetDetails?.assetAttributes
      .map((item) => item.type)
      .includes(newAttributeData.type);
    const filteredSecondArray = defaultAvailableAttributeName?.filter(
      (item) => item !== newAttributeData.type
    );
    setCurrentAvailableAttributes(filteredSecondArray);
    if (newAttributeData.type !== "" && newAttributeData.value !== "") {
      if (!uniqueName) {
        setAssetDetails((prevState) => {
          if (prevState?.assetAttributes) {
            return {
              ...prevState,
              assetAttributes: [
                ...prevState.assetAttributes,
                newAttributeData,
              ],
            };
          } else {
            return {
              ...prevState,
              assetAttributes: [newAttributeData],
            };
          }
        });
        setAsset({
          type: "",
          value: "",
        });
      } else {
        callSnackbar("error", getErrorMessage(t("genset.asset.uniqueType")));
      }
    } else {
      callSnackbar("error", getErrorMessage(t("genset.asset.mandatoryFields")));
    }
  };
  const handleEditAsset = (data) => {
    setEditAssetData(data);
    setAssetDetails((prevState) => {
      const enableAsset = prevState.assetAttributes.map((asset) => {
        if (asset.id === data.id) {
          focusInput(asset.id);
          return { ...asset, isEdit: true };
        } else {
          return { ...asset, isEdit: false };
        }
      });
      return {
        ...prevState,
        assetAttributes: enableAsset,
      };
    });
  };
  const handleCancelUpdateAsset = (assetData) => {
    setAssetDetails((prevState) => {
      const updatedAsset = prevState.assetAttributes.map((asset) => {
        if (asset.id === assetData.id) {
          return { ...editAssetData, isEdit: false };
        }
        return { ...asset, isEdit: false };
      });

      return {
        ...prevState,
        assetAttributes: updatedAsset,
      };
    });
  };
  const handleOkEditAsset = () => {
    setAssetDetails((prevState) => {
      const enableAsset = prevState.assetAttributes.map((asset) => {
        if (asset.value === "") {
          callSnackbar(
            "error",
            t("genset.asset.mandatoryFields")
          );
          return { ...asset };
        } else {
          return { ...asset, isEdit: false };
        }
      });

      return {
        ...prevState,
        assetAttributes: enableAsset,
      };
    });
  };
  const handleDeleteAsset = (id) => {
    setAssetDetails((prevState) => {
      const updateAsset = prevState.assetAttributes.filter(
        (asset) => asset.id !== id
      );
      return {
        ...prevState,
        assetAttributes: updateAsset,
      };
    });
  };

  const handleInputChange = (category, key, value) => {
    setProjectComponents((prev) => {
      const newState = { ...prev, currentComponent: "genset" };
      if (category === "incentives") {
        set(newState, `genset.${category}`, key);
      } else {
        set(newState, `genset.${category}.gensetParameters.${key}`, value);
      }
      return newState;
    });
    // Update form field directly to preserve other field errors
    if (category === "incentives") {
      form.setFieldValue("incentives", key);
    } else if (category === "componentAttributes") {
      form.setFieldValue(`componentAttributes.gensetParameters.${key}`, value);
    } else if (category === "economicAttributes") {
      form.setFieldValue(`economicAttributes.gensetParameters.${key}`, value);
    } else if (category === "uiAttributes") {
      form.setFieldValue(`uiAttributes.gensetParameters.${key}`, value);
    }
    setComponentStatus((prev) => ({
      ...prev,
      genset: { isEditing: true, completed: false },
    }));
  };

  useEffect(() => {
    if (!form.isDirty()) return;
    setComponentStatus((prev) => ({
      ...prev,
      genset: { isEditing: true, completed: false },
    }));
  }, [form.values]);

  const handleSelectBatteryChange = (value) => {
    if (value === 0) {
      setCustomButton(true);
    } else {
      setCustomButton(false);

      const selectedBatteryId = value;
      if (!selectedBatteryId) return;
      const matchedAsset = batteryListData.find(
        (asset) => asset.assetId === selectedBatteryId
      );
      if (!matchedAsset) return;
      const assetAttributes = matchedAsset.assetAttributes || [];
      const getAttributeValue = (type, defaultValue = 0) => {
        const attr = assetAttributes.find((attr) => attr.type === type);
        return attr ? Number(attr.value) : "";
      };
      const updatedBatteryState = {
        ...projectComponents,
        genset: {
          ...projectComponents.genset,
          componentAttributes: {
            ...projectComponents.genset.componentAttributes,
            gensetParameters: {
              ...projectComponents.genset.componentAttributes.gensetParameters,
              DegradationFactor: getAttributeValue("DegradationFactor")||projectComponents?.genset?.componentAttributes?.gensetParameters?.DegradationFactor,
              MinimumRunTime: getAttributeValue("MinimumRunTime")||projectComponents?.genset?.componentAttributes?.gensetParameters?.MinimumRunTime,
              MinPowerRate: getAttributeValue("MinPowerRate")||projectComponents?.genset?.componentAttributes?.gensetParameters?.MinPowerRate,
              PowElecNom: getAttributeValue("PowElecNom")||projectComponents?.genset?.componentAttributes?.gensetParameters?.PowElecNom,
              PowerProfile: "Power",
            },
          },
          economicAttributes: {
            ...projectComponents.genset.economicAttributes,
            gensetParameters: {
              ...projectComponents.genset.economicAttributes.gensetParameters,
              LifetimeCycle: getAttributeValue("LifetimeCycle"),
            },
          },
        },
      };
      setProjectComponents((prev) => {
        if (
          JSON.stringify(prev.genset) !==
          JSON.stringify(updatedBatteryState.genset)
        ) {
          form.setValues(updatedBatteryState?.genset);
          return updatedBatteryState;
        }
         form.setValues(prev?.genset);
        return prev;
      });
      setComponentStatus((prev) => ({
        ...prev,
        genset: { isEditing: true, completed: false },
      }));
    }
  };

  const SaveGenset = () => {
      // Guard: abort save if any active incentive attribute is invalid
      const hasIncentiveErrors = () => {
        const list = projectComponents?.genset?.incentives || incentives || [];
        return list.some((item) => {
          if (!item?.status) return false; // only validate selected incentives
          return item?.incentiveAttributes?.some((inc) => {
            const valueStr = (inc?.attributeDefaultValue ?? "").toString().trim();
            if (valueStr === "") return true; // required empty
            if (inc?.attributeRegex) {
              try {
                const regex = new RegExp(inc.attributeRegex);
                return !regex.test(valueStr);
              } catch (e) {
                // malformed regex -> ignore pattern failure
                return false;
              }
            }
            return false;
          });
        });
      };
      if (hasIncentiveErrors()) {
        displayError("error", t("genset.validation.incentiveErrors"));
        return;
      }
    setLoadingComp(true);
    const filteredComponent = selectedDesignAndDesignVersion.componentDTOList.find(
      (comp) => comp.componentType === "Baseload genset"
    );
    const payload = {
      projectId,
      designId: designId,
      designVersionId: designVersionId,
      componentType: "Baseload genset",
      componentId: filteredComponent ? filteredComponent.componentId : -1,
      componentAttributes: {
        gensetParameters: {
          HeatRecoveryRatio:
          projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="ControlledGenset"?0:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.HeatRecoveryRatio || 0,
          ThermalEmissionFactor:projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="ControlledGenset"? 0 :
          projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.ThermalEmissionFactor || 0,
          ThermalEnergyPrice:projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="ControlledGenset"? 0 :
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.ThermalEnergyPrice || 0,
          PowerReserve:
            projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="BaseloadGenset"? 0:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.PowerReserve || 0,
          PowElecNom:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.PowElecNom || 0,
          DegradationFactor:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.DegradationFactor,
          MinPowerRate:
          projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="BaseloadGenset"? 0:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.MinPowerRate || 0,
          FuelCurveSlope:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.FuelCurveSlope || 0,
          FuelCurveIntercept:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.FuelCurveIntercept || 0,
          MinimumRunTime:
          projectComponents?.genset?.componentAttributes?.gensetParameters
            ?.GensetType==="BaseloadGenset" ? 0:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.MinimumRunTime || 0.1,
          PowerProfile: "Power",
          Fuel: {
            Name: projectComponents?.genset?.componentAttributes
              ?.gensetParameters?.Fuel?.Name,
            PricePerM3:
              projectComponents?.genset?.componentAttributes?.gensetParameters
                ?.Fuel?.PricePerM3,
            LHV: projectComponents?.genset?.componentAttributes
              ?.gensetParameters?.Fuel?.LHV,
            Density:
              projectComponents?.genset?.componentAttributes?.gensetParameters
                ?.Fuel?.Density,
            Carbon:
              projectComponents?.genset?.componentAttributes?.gensetParameters
                ?.Fuel?.Carbon,
          },
          NbGenset:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.NbGenset,
          GensetType:
            projectComponents?.genset?.componentAttributes?.gensetParameters
              ?.GensetType || "BaseloadGenset",    
        },
      },
      economicAttributes: {
        gensetParameters: {
          CAPEX:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.CAPEX || 0,
          Replacement:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.Replacement || 0,
          LifetimeHour:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.LifetimeHour || 0,
          LifetimeYear:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.LifetimeYear || 0,
          OM:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.OM || 0,
          InflationRate:
            projectComponents?.genset?.economicAttributes?.gensetParameters
              ?.InflationRate || 0,
        },
      },
      datasource: "OTHER",
      uiAttributes: {
        gensetParameters: {
          selectGenset:
            projectComponents?.genset?.uiAttributes?.gensetParameters
              ?.selectGenset || "Custom",
          assetId:
            projectComponents?.genset?.uiAttributes?.gensetParameters
              ?.assetId ?? 0,
          gensetEconomicOptimization:
            projectComponents?.genset?.uiAttributes?.gensetParameters
              ?.gensetEconomicOptimization ?? 0,
        },
      },
      incentives: projectComponents?.genset?.incentives || [],
    };
    MGA_API.saveLoadComponent(payload)
      .then((res) => {
        setComponentStatus((prev) => ({
          ...prev,
          genset: { isEditing: false, completed: true },
        }));
        MGA_API.editProject(
          projectId
        ).then((res) => {
          setProjectDetails((prev) => ({
            ...prev,
            userId: res.userId || "",
            currencyCode: res?.projectDTO?.currencyCode || "$",
            projectDTO: res.projectDTO || {},
            componentDTOList:
              flattenCountComponentDTOList(res.countComponentDTOList) || [],
            designWithVersionsDTOList: res.designWithVersionsDTOList || [],
            locationDTO: res.locationDTO || {},
            countComponentDTOList: res.countComponentDTOList,
            designComponentsList: flattenDesignCountComponent(res?.designComponentsList) || [],
          }));
        });
        setLoadingComp(false);
        displayError("success", t("genset.save.success"));

        // Thermal load / genset type cross-validation
        const savedGensetType = payload.componentAttributes.gensetParameters.GensetType;
        const hasThermalLoad = selectedDesignAndDesignVersion.countComponentDTOList.some(
          (c) => c.componentType === "ThermalLoad"
        );
        const loadAPIData = _.find(
           selectedDesignAndDesignVersion.componentDTOList,
           {
             componentType: "ElectricalLoad",
           }
         );
        const hasAshraeLoad = loadAPIData?.datasource?.toLowerCase() === "ashrae";
        if ((hasThermalLoad || hasAshraeLoad) && savedGensetType === "ControlledGenset") {
          setThermalValidationModal({ open: true, type: "controlled_with_thermal" });
        } else if (!hasThermalLoad&& !hasAshraeLoad && savedGensetType === "BaseloadGenset") {
          setThermalValidationModal({ open: true, type: "baseload_without_thermal" });
        }
      })
      .catch((error) => {
        setLoadingComp(false);
        console.error("Error fetching data:", error);
      });
  };

  const header1 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("genset") + 1}`}: A.
      </span>
      <span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("genset.header.configure")}
      </span>
    </>
  );

    const header2 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("genset") + 1}`}: B.
      </span>
      <span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp; {t("genset.investmentCharges")}
      </span>
    </>
  );

  const toggleDetailVisibility = (id) => {
    setVisibleDetails((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const header3 = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("genset") + 1}`}: C.
      </span>
      <span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        &nbsp;{t("genset.header.incentives")}
      </span>
    </>
  );

  const setFuelDependantFields = (updatedValues, slopeVal) => {
    setProjectComponents((prev) => ({
      ...prev,
      currentComponent: "genset",
      genset: {
        ...prev.genset,
        componentAttributes: {
          ...prev.genset?.componentAttributes,
          gensetParameters: {
            ...prev.genset?.componentAttributes?.gensetParameters,
            FuelCurveSlope: slopeVal,
            Fuel: {
              ...prev.genset?.componentAttributes?.gensetParameters?.Fuel,
              ...updatedValues,
            },
          },
        },
      },
    }));
    // Reflect changes into Mantine form to avoid clearing other errors
    if (typeof slopeVal !== "undefined" && slopeVal !== null) {
      form.setFieldValue("componentAttributes.gensetParameters.FuelCurveSlope", slopeVal);
    }
    Object.entries(updatedValues || {}).forEach(([k, v]) => {
      form.setFieldValue(`componentAttributes.gensetParameters.Fuel.${k}`, v);
    });
  };

  const handleFuelChange = (value) => {
    switch (value) {
      case "Diesel":
        setFuelDependantFields(
          {
            Name: value,
            LHV: "43.2",
            Density: "820",
            Carbon: "88",
          },
          0.29
        );
        break;
      case "Biofuel":
        setFuelDependantFields(
          {
            Name: value,
            LHV: "5.5",
            Density: "0.72",
            Carbon: "2",
          },
          0.2
        );
        break;
      case "Natural Gas":
        setFuelDependantFields(
          {
            Name: value,
            LHV: "43",
            Density: "0.79",
            Carbon: "67",
          },
          0.3
        );
        break;
      default:
        setFuelDependantFields({
          Name: value,
          LHV: "",
          Density: "",
          Carbon: "",
        });
    }
  };  

  return (
    <div ref={containerRef}>
      <SnackBarJs snackBar={snackBar} ref={snackBarRef} />
      <AccordionItem id={1} header={header1()} key={1} height="fit-content">
        <GensetConfigWrapper
          batteryList={batteryList}
          gensetParameters={gensetParameters}
          handleSelectBatteryChange={handleSelectBatteryChange}
          handleInputChange={handleInputChange}
          customButton={customButton}
          addAsset={addAsset}
          form={form}
          popupStatus={popupStatus}
          addStatus={addStatus}
          assetDetails={assetDetails}
          assetInfo={assetInfo}
          updatedAsset={updatedAsset}
          onCloseAssetPopUp={onCloseAssetPopUp}
          onUpdateAsset={onUpdateAsset}
          getAssetDetailsAttribute={getAssetDetailsAttribute}
          addAttributeToLibrary={addAttributeToLibrary}
          handleAsset={handleAsset}
          handleAssetSelection={handleAssetSelection}
          asset={asset}
          SaveGenset={SaveGenset}
          currentAvailableAttributes={currentAvailableAttributes}
          isExtendedLibrary={isExtendedLibrary}
          getDefaultAssetsAttributeList={getDefaultAssetsAttributeList}
          projectTypes={projectTypes}
          handleEditAsset={handleEditAsset}
          handleDeleteAsset={handleDeleteAsset}
          handleAssetAttribute={handleAssetAttribute}
          handleOkEditAsset={handleOkEditAsset}
          handleCancelUpdateAsset={handleCancelUpdateAsset}
          setIsExtendedLibrary={setIsExtendedLibrary}
          inputRefs={inputRefs}
          handleFuelChange={handleFuelChange}
          setCurrentAvailableAttributes={setCurrentAvailableAttributes}
        />
      </AccordionItem>
      <AccordionItem id={1} header={header2()} key={2} height="fit-content">
        <>
          <Grid gutter="md" className="mt-2 mb-2">
            <Grid.Col {...responsiveCol}>
              <TextInput
                {...getFieldAttributes("field-17")}
                data-id="field-17"
                {...form.getInputProps(
                  "economicAttributes.gensetParameters.CAPEX",
                )}
                onChange={(event) => {
                  const value = formatInputValue(event.target.value);
                  handleInputChange("economicAttributes", "CAPEX", value);
                }}
                label={t("genset.capex.label")}
                required
                placeholder={t("genset.capex.placeholder")}
                rightSection={
                  <div style={grayIconStyle}>
                    {projectDetails?.projectDTO?.currencyCode || "$"}/kW
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                rightSectionWidth={60}
              />
            </Grid.Col>
             <Grid.Col {...responsiveCol}>
              <TextInput
                {...getFieldAttributes("field-19")}
                data-id="field-19"
                {...form.getInputProps(
                  "economicAttributes.gensetParameters.OM",
                )}
                onChange={(event) => {
                  const value = formatInputValue(event.target.value);
                  handleInputChange("economicAttributes", "OM", value);
                }}
                label={t("genset.om.label")}
                required
                placeholder={t("genset.om.placeholder")}
                rightSection={
                  <div style={grayIconStyle}>
                    {projectDetails?.projectDTO?.currencyCode || "$"}/kW
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                rightSectionWidth={60}
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <TextInput
                {...getFieldAttributes("field-18")}
                data-id="field-18"
                label={t("genset.replacement.label")}
                {...form.getInputProps(
                  "economicAttributes.gensetParameters.Replacement",
                )}
                onChange={(event) => {
                  const value = formatInputValue(event.target.value);
                  handleInputChange("economicAttributes", "Replacement", value);
                }}
                required
                placeholder={t("genset.replacement.placeholder")}
                rightSection={
                  <div style={grayIconStyle}>
                    {projectDetails?.projectDTO?.currencyCode || "$"}/kW
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                rightSectionWidth={60}
              />
            </Grid.Col>
                    <Grid.Col {...responsiveCol}>
                      <TextInput
                        onClick={e => e.stopPropagation()}
                        {...getFieldAttributes("field-6")}
                        data-id="field-6"
                        label={t("genset.lifeTimeYears.label")}
                        required
                        {...form.getInputProps(
                          "economicAttributes.gensetParameters.LifetimeYear"
                        )}
                        onChange={(e) => {
                          const trimmedValue = e.currentTarget.value
                            .trim()
                          handleInputChange(
                            "economicAttributes",
                            "LifetimeYear",
                            trimmedValue
                          );
                        }}
                        placeholder={t("genset.lifeTimeYears.placeholder")}
                        rightSection={
                          <div style={{ ...grayIconStyle, marginRight: "1rem" }}>Years</div>
                        }
                        rightSectionProps={{ style: { pointerEvents: "none" } }}
                      />
                    </Grid.Col>
           
          </Grid>
          <Grid gutter="md" className="mt-2 mb-2">
            <Grid.Col {...responsiveCol}>
              <TextInput
                {...getFieldAttributes("field-20")}
                data-id="field-20"
                {...form.getInputProps(
                  "economicAttributes.gensetParameters.InflationRate",
                )}
                onChange={(e) => {
                  const trimmedValue = formatInputValue(e.currentTarget.value);
                  handleInputChange(
                    "economicAttributes",
                    "InflationRate",
                    trimmedValue,
                  );
                }}
                label={t("genset.inflation.label")}
                required
                placeholder={t("genset.inflation.placeholder")}
                rightSection={
                  <div style={{ ...grayIconStyle }}>
                    <IconPercentage size={18} />
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>
          </Grid>
        </>
      </AccordionItem>
      <AccordionItem
        id={3}
        header={header3()}
        key={345658}
        height="fit-content"
        children={
          <IncentivesTable
            incentives={incentives}
            toggleDetailVisibility={toggleDetailVisibility}
            visibleDetails={visibleDetails}
            handleInputChange={(data) => handleInputChange("incentives", data)}
            setIncentives={setIncentives}
          />
        }
      />
      <SEConfirmModal
        opened={thermalValidationModal.open}
        onClose={() => setThermalValidationModal({ open: false, type: null })}
        onConfirm={() => {
          setThermalValidationModal({ open: false, type: null });
          if (thermalValidationModal.type === "baseload_without_thermal") {
            if (onNavigateToComponent) {
              onNavigateToComponent("load");
            } else {
              setNavigateToComponent("load");
            }
          }
          // For "controlled_with_thermal": stay on genset panel so user can switch to Baseload
        }}
        header={t("genset.thermalValidation.header")}
        message={
          thermalValidationModal.type === "controlled_with_thermal" ? (
            <div>
              <p style={{ marginBottom: "0.75rem" }}>
                {t("genset.thermalValidation.controlledWithThermal")}
              </p>
              <div style={{ border: "1px solid white", padding: "0.6rem 0.75rem", borderRadius: "4px" }}>
                <strong>{t("common.note")}</strong>
                <ul style={{ margin: "0.4rem 0 0 0" }}>
                  <li><strong>{t("common.continue")}</strong> → {t("genset.thermalValidation.note.controlled.continue")}</li>
                  <li><strong>{t("common.yes")}</strong> → {t("genset.thermalValidation.note.controlled.yes")}</li>
                </ul>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: "0.75rem" }}>
                {t("genset.thermalValidation.baseloadWithoutThermal")}
              </p>
              <div style={{ border: "1px solid white", padding: "0.6rem 0.75rem", borderRadius: "4px" }}>
                <strong>{t("common.note")}</strong>
                <ul style={{ margin: "0.4rem 0 0 0", paddingLeft: "1.2rem" }}>
                  <li><strong>{t("common.continue")}</strong> → {t("genset.thermalValidation.note.baseload.continue")}</li>
                  <li><strong>{t("common.yes")}</strong> → {t("genset.thermalValidation.note.baseload.yes")}</li>
                </ul>
              </div>
            </div>
          )
        }
        confirmLabel={t("common.yes")}
        cancelLabel={t("common.continue")}
        showCancel
      />
    </div>
  );
});

export default GensetConfiguration;

GensetConfiguration.propTypes = {
  setLoadingComp: PropTypes.func,
};
